import { useState, useCallback, useRef, useMemo } from 'react';
import {
  ReactFlow,
  Background,
  Controls,
  MiniMap,
  addEdge,
  useNodesState,
  useEdgesState,
  ReactFlowProvider,
} from '@xyflow/react';
import { Button, Space, Typography, Input, message, Tooltip, Badge } from 'antd';
import {
  PlayCircleOutlined,
  SaveOutlined,
  FolderOpenOutlined,
  DeploymentUnitOutlined,
  ApiOutlined,
  BranchesOutlined,
  FlagOutlined,
  PlusOutlined,
  ClearOutlined,
} from '@ant-design/icons';
import AgentNode from '../../components/AgentNode';
import '@xyflow/react/dist/style.css';

/* ═══════════════════════════════════════════════════
   Agent Orchestration Canvas — Agent DAG 编排画布

   功能:
   - ReactFlow 画布: 自定义节点/边/背景/控制/小地图
   - 左侧侧边栏: 可拖拽节点类型 (开始/Agent/条件/结束)
   - 顶部工具栏: 执行/保存/加载 + 标题
   - 底部思考日志: 流式展示 Agent 执行推理过程
   - localStorage DAG 持久化
   - 模拟执行: 节点依次 running → done 动画

   设计: 荧夜蓝 + 萤火暖金 (复用全局 design token)
   ═══════════════════════════════════════════════════ */

const { Title, Text } = Typography;

/* ── 节点类型定义 (侧边栏拖拽源) ── */
const NODE_TYPES = [
  {
    type: 'start',
    label: '开始',
    icon: <FlagOutlined />,
    color: '#22c55e',
    desc: 'DAG 起始节点',
    defaultData: { label: '开始', icon: '🚀', desc: 'DAG 流程起点' },
  },
  {
    type: 'agent',
    label: 'Agent',
    icon: <DeploymentUnitOutlined />,
    color: '#6366f1',
    desc: 'AI Agent 执行节点',
    defaultData: { label: 'Agent', icon: '🤖', desc: '执行智能任务' },
  },
  {
    type: 'condition',
    label: '条件',
    icon: <BranchesOutlined />,
    color: '#f59e0b',
    desc: '条件分支判断',
    defaultData: { label: '条件判断', icon: '🔀', desc: '根据条件选择分支' },
  },
  {
    type: 'end',
    label: '结束',
    icon: <ApiOutlined />,
    color: '#ef4444',
    desc: 'DAG 终止节点',
    defaultData: { label: '结束', icon: '🏁', desc: 'DAG 流程终点' },
  },
];

/* ── 自定义节点注册 ── */
const nodeTypes = { agent: AgentNode };

/* ── 初始 DAG 配置 ── */
const INITIAL_NODES = [
  {
    id: 'node-1',
    type: 'agent',
    position: { x: 280, y: 60 },
    data: { label: '意图解析', icon: '🎯', desc: '解析自然语言场景需求，提取约束因子', status: 'idle' },
  },
  {
    id: 'node-2',
    type: 'agent',
    position: { x: 100, y: 220 },
    data: { label: 'POI 检索', icon: '📍', desc: '地点检索与筛选', status: 'idle' },
  },
  {
    id: 'node-3',
    type: 'agent',
    position: { x: 460, y: 220 },
    data: { label: '预算分析', icon: '💰', desc: '预算约束与优化', status: 'idle' },
  },
  {
    id: 'node-4',
    type: 'agent',
    position: { x: 280, y: 380 },
    data: { label: '决策解释', icon: '🔍', desc: '解释推荐理由和拒绝原因', status: 'idle' },
  },
];

const INITIAL_EDGES = [
  { id: 'e1-2', source: 'node-1', target: 'node-2', animated: false, style: { stroke: 'var(--accent)', strokeWidth: 2 } },
  { id: 'e1-3', source: 'node-1', target: 'node-3', animated: false, style: { stroke: 'var(--accent)', strokeWidth: 2 } },
  { id: 'e2-4', source: 'node-2', target: 'node-4', animated: false, style: { stroke: 'var(--accent)', strokeWidth: 2 } },
  { id: 'e3-4', source: 'node-3', target: 'node-4', animated: false, style: { stroke: 'var(--accent)', strokeWidth: 2 } },
];

const STORAGE_KEY = 'agent-dag-config';

/* ── 思考日志模板 ── */
const THINKING_TEMPLATES = [
  '正在解析用户意图...',
  '构建决策上下文 DecisionContext...',
  '检索相关 POI 数据...',
  '执行预算约束分析...',
  '生成候选方案集...',
  '仿真校验方案可行性...',
  '构建解释链...',
  '汇总最终输出...',
];

/* ═══════════════════════════════════════════════════
   主组件
   ═══════════════════════════════════════════════════ */
function AgentOrchestrationInner() {
  const [nodes, setNodes, onNodesChange] = useNodesState(INITIAL_NODES);
  const [edges, setEdges, onEdgesChange] = useEdgesState(INITIAL_EDGES);
  const [isExecuting, setIsExecuting] = useState(false);
  const [logs, setLogs] = useState([]);
  const [logKey, setLogKey] = useState(0);
  const reactFlowWrapper = useRef(null);
  const [reactFlowInstance, setReactFlowInstance] = useState(null);

  /* ── 连接创建 ── */
  const onConnect = useCallback(
    (params) =>
      setEdges((eds) =>
        addEdge(
          {
            ...params,
            animated: false,
            style: { stroke: 'var(--accent)', strokeWidth: 2 },
          },
          eds
        )
      ),
    [setEdges]
  );

  /* ── 拖拽添加节点 ── */
  const onDragOver = useCallback((event) => {
    event.preventDefault();
    event.dataTransfer.dropEffect = 'move';
  }, []);

  const onDrop = useCallback(
    (event) => {
      event.preventDefault();
      const type = event.dataTransfer.getData('application/reactflow');
      if (!type || !reactFlowInstance) return;

      const bounds = reactFlowWrapper.current.getBoundingClientRect();
      const position = reactFlowInstance.screenToFlowPosition({
        x: event.clientX - bounds.left,
        y: event.clientY - bounds.top,
      });

      const meta = NODE_TYPES.find((t) => t.type === type) || NODE_TYPES[1];
      const id = `node-${Date.now()}`;
      const newNode = {
        id,
        type: 'agent',
        position,
        data: { ...meta.defaultData, status: 'idle' },
      };

      setNodes((nds) => nds.concat(newNode));
    },
    [reactFlowInstance, setNodes]
  );

  /* ── 保存 DAG 到 localStorage ── */
  const handleSave = useCallback(() => {
    if (!reactFlowInstance) return;
    const flow = reactFlowInstance.toObject();
    localStorage.setItem(STORAGE_KEY, JSON.stringify(flow));
    message.success('DAG 已保存');
  }, [reactFlowInstance]);

  /* ── 从 localStorage 加载 DAG ── */
  const handleLoad = useCallback(() => {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) {
      message.warning('暂无保存的 DAG');
      return;
    }
    try {
      const flow = JSON.parse(raw);
      setNodes(flow.nodes || []);
      setEdges(flow.edges || []);
      message.success('DAG 已加载');
    } catch {
      message.error('加载失败，配置已损坏');
    }
  }, [setNodes, setEdges]);

  /* ── 清空画布 ── */
  const handleClear = useCallback(() => {
    setNodes([]);
    setEdges([]);
    setLogs([]);
  }, [setNodes, setEdges]);

  /* ── 模拟执行: 节点依次 running → done ── */
  const handleExecute = useCallback(async () => {
    if (isExecuting) return;
    if (nodes.length === 0) {
      message.warning('画布为空，请先添加节点');
      return;
    }

    setIsExecuting(true);
    setLogs([]);
    setLogKey((k) => k + 1);

    // 重置所有节点状态为 idle
    setNodes((nds) =>
      nds.map((n) => ({ ...n, data: { ...n.data, status: 'idle' } }))
    );

    const newLogs = [];
    const addLog = (text) => {
      newLogs.push({ id: newLogs.length, text, time: new Date().toLocaleTimeString() });
      setLogs([...newLogs]);
    };

    // 按顺序激活每个节点
    for (let i = 0; i < nodes.length; i++) {
      const node = nodes[i];

      // 节点进入 running
      setNodes((nds) =>
        nds.map((n) =>
          n.id === node.id
            ? { ...n, data: { ...n.data, status: 'running' } }
            : n
        )
      );

      const thinking = THINKING_TEMPLATES[i % THINKING_TEMPLATES.length];
      addLog(`[${node.data.label}] ${thinking}`);

      // 模拟执行耗时
      await new Promise((resolve) => setTimeout(resolve, 1200 + Math.random() * 800));

      // 节点完成
      setNodes((nds) =>
        nds.map((n) =>
          n.id === node.id
            ? { ...n, data: { ...n.data, status: 'done' } }
            : n
        )
      );
      addLog(`[${node.data.label}] 执行完成 ✓`);
    }

    addLog('— DAG 执行完毕 —');
    setIsExecuting(false);
  }, [isExecuting, nodes, setNodes]);

  /* ── MiniMap 节点颜色 ── */
  const minimapNodeColor = useCallback((node) => {
    const status = node.data?.status;
    if (status === 'done') return '#22c55e';
    if (status === 'running') return '#e8d28a';
    return 'var(--text-muted)';
  }, []);

  /* ── 侧边栏拖拽开始 ── */
  const onDragStart = useCallback((event, nodeType) => {
    event.dataTransfer.setData('application/reactflow', nodeType);
    event.dataTransfer.effectAllowed = 'move';
  }, []);

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        height: 'calc(100vh - 64px)',
        background: 'transparent',
        position: 'relative',
      }}
    >
      {/* ════════ 顶部工具栏 ════════ */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '10px 20px',
          borderBottom: '1px solid var(--border-default)',
          background: 'rgba(6, 18, 38, 0.85)',
          backdropFilter: 'blur(12px)',
          zIndex: 10,
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <DeploymentUnitOutlined style={{ fontSize: 20, color: 'var(--accent)' }} />
          <Title level={4} style={{ margin: 0, color: 'var(--text-primary)', fontSize: 16, fontWeight: 600 }}>
            Agent 编排画布
          </Title>
          <Badge
            count={nodes.length}
            style={{ backgroundColor: 'rgba(232, 210, 138, 0.15)', color: 'var(--accent)', border: '1px solid rgba(232, 210, 138, 0.3)' }}
          />
        </div>

        <Space size={8}>
          <Button
            type="primary"
            icon={<PlayCircleOutlined />}
            onClick={handleExecute}
            loading={isExecuting}
            style={{
              background: isExecuting ? 'rgba(232, 210, 138, 0.2)' : 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
              border: 'none',
              fontWeight: 600,
            }}
          >
            {isExecuting ? '执行中...' : '执行'}
          </Button>
          <Tooltip title="保存 DAG 到本地">
            <Button icon={<SaveOutlined />} onClick={handleSave} style={btnStyle}>保存</Button>
          </Tooltip>
          <Tooltip title="从本地加载 DAG">
            <Button icon={<FolderOpenOutlined />} onClick={handleLoad} style={btnStyle}>加载</Button>
          </Tooltip>
          <Tooltip title="清空画布">
            <Button icon={<ClearOutlined />} onClick={handleClear} style={btnStyle} danger />
          </Tooltip>
        </Space>
      </div>

      {/* ════════ 主内容区 ════════ */}
      <div style={{ display: 'flex', flex: 1, overflow: 'hidden' }}>
        {/* ── 左侧侧边栏: 节点类型列表 ── */}
        <div
          style={{
            width: 200,
            borderRight: '1px solid var(--border-default)',
            background: 'rgba(6, 18, 38, 0.6)',
            padding: 16,
            display: 'flex',
            flexDirection: 'column',
            gap: 8,
            overflowY: 'auto',
            flexShrink: 0,
          }}
        >
          <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-secondary)', marginBottom: 4, letterSpacing: '0.03em' }}>
            节点类型
          </div>
          {NODE_TYPES.map((item) => (
            <div
              key={item.type}
              draggable
              onDragStart={(e) => onDragStart(e, item.type)}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 10,
                padding: '10px 12px',
                borderRadius: 10,
                border: '1px solid var(--border-default)',
                background: 'var(--surface-1)',
                cursor: 'grab',
                transition: 'all 200ms var(--ease)',
                userSelect: 'none',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.borderColor = item.color;
                e.currentTarget.style.background = `color-mix(in srgb, ${item.color} 8%, var(--surface-1))`;
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.borderColor = 'var(--border-default)';
                e.currentTarget.style.background = 'var(--surface-1)';
              }}
            >
              <div
                style={{
                  width: 32,
                  height: 32,
                  borderRadius: 8,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  background: `color-mix(in srgb, ${item.color} 12%, transparent)`,
                  color: item.color,
                  fontSize: 16,
                  flexShrink: 0,
                }}
              >
                {item.icon}
              </div>
              <div>
                <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-primary)' }}>{item.label}</div>
                <div style={{ fontSize: 10, color: 'var(--text-muted)' }}>{item.desc}</div>
              </div>
            </div>
          ))}

          <div style={{ marginTop: 16, padding: '12px', borderRadius: 10, background: 'var(--accent-soft)', border: '1px solid rgba(232, 210, 138, 0.1)' }}>
            <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--accent)', marginBottom: 6 }}>使用说明</div>
            <div style={{ fontSize: 10, color: 'var(--text-secondary)', lineHeight: 1.6 }}>
              • 拖拽节点到画布<br />
              • 连接: 从底部 Handle 拖到顶部 Handle<br />
              • 点击「执行」模拟 DAG 运行
            </div>
          </div>
        </div>

        {/* ── 中间画布 + 底部日志 ── */}
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
          {/* ReactFlow 画布 */}
          <div ref={reactFlowWrapper} style={{ flex: 1, position: 'relative' }}>
            <ReactFlow
              nodes={nodes}
              edges={edges}
              onNodesChange={onNodesChange}
              onEdgesChange={onEdgesChange}
              onConnect={onConnect}
              onInit={setReactFlowInstance}
              onDrop={onDrop}
              onDragOver={onDragOver}
              nodeTypes={nodeTypes}
              fitView
              /* 透出 #root::before 的森林背景（登录页原图） */
              style={{ background: 'transparent' }}
              defaultEdgeOptions={{
                style: { stroke: 'var(--accent)', strokeWidth: 2 },
              }}
            >
              <Background color="rgba(255,255,255,0.04)" gap={24} size={1} />
              <Controls
                style={{
                  background: 'var(--surface-1)',
                  border: '1px solid var(--border-default)',
                  borderRadius: 10,
                  boxShadow: 'var(--shadow-md)',
                }}
              />
              <MiniMap
                nodeColor={minimapNodeColor}
                style={{
                  background: 'var(--surface-1)',
                  border: '1px solid var(--border-default)',
                  borderRadius: 10,
                }}
                maskColor="rgba(6, 18, 38, 0.6)"
              />
            </ReactFlow>
          </div>

          {/* ── 底部思考日志区 ── */}
          <div
            style={{
              height: 200,
              borderTop: '1px solid var(--border-default)',
              background: 'rgba(3, 11, 24, 0.85)',
              display: 'flex',
              flexDirection: 'column',
              flexShrink: 0,
            }}
          >
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                padding: '8px 16px',
                borderBottom: '1px solid var(--border-subtle)',
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-secondary)' }}>
                  思考日志
                </span>
                {isExecuting && (
                  <span
                    style={{
                      fontSize: 10,
                      color: 'var(--accent)',
                      padding: '2px 8px',
                      borderRadius: 4,
                      background: 'rgba(232, 210, 138, 0.1)',
                      border: '1px solid rgba(232, 210, 138, 0.2)',
                      animation: 'ao-pulse 1.8s ease-in-out infinite',
                    }}>
                    推理中...
                  </span>
                )}
              </div>
              {logs.length > 0 && (
                <Text style={{ fontSize: 10, color: 'var(--text-muted)' }}>
                  {logs.length} 条记录
                </Text>
              )}
            </div>

            <div
              style={{
                flex: 1,
                overflowY: 'auto',
                padding: '8px 16px',
                fontFamily: 'var(--font-mono)',
                fontSize: 12,
                lineHeight: 1.8,
              }}
            >
              {logs.length === 0 ? (
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', color: 'var(--text-muted)', fontSize: 11 }}>
                  点击「执行」按钮开始推理...
                </div>
              ) : (
                logs.map((log) => (
                  <div key={log.id} style={{ color: 'var(--text-secondary)', display: 'flex', gap: 8 }}>
                    <span style={{ color: 'var(--text-muted)', flexShrink: 0, fontSize: 10 }}>{log.time}</span>
                    <span style={{ color: log.text.includes('✓') ? '#22c55e' : log.text.includes('完毕') ? 'var(--accent)' : 'var(--text-secondary)' }}>
                      {log.text}
                    </span>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ── 按钮统一样式 ── */
const btnStyle = {
  background: 'rgba(255, 255, 255, 0.04)',
  border: '1px solid var(--border-default)',
  color: 'var(--text-secondary)',
};

/* ═══════════════════════════════════════════════════
   导出 — 包裹 ReactFlowProvider
   ═══════════════════════════════════════════════════ */
export default function AgentOrchestration() {
  return (
    <ReactFlowProvider>
      <AgentOrchestrationInner />
    </ReactFlowProvider>
  );
}
